import { useEffect, useCallback, useState } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
  NavLink,
} from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { format } from 'date-fns';
import {
  ButtonSecondary,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  ButtonPrimary,
  InputText,
  SidePanelRow,
  Select,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionDenied,
  PermissionEnum,
  IconChangeLoClock,
  Currency,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  changePanelState,
  clearRateSidepanelState,
  setRateHistory,
  setExpandedRateHistoryId,
} from './store/slices/sidepanel';
import {
  useCreateRateMutation,
  useUpdateRateMutation,
  useLazyGetExchangePairRatesQuery,
  useLazyGetRateQuery,
  useLazyGetRateHistoryDetailsQuery,
  useLazyGetExchangeRatesQuery,
} from './store/services';
import HistoryRateList from './history-list';
import { RateFormValues } from './types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const RateDetails = (): React.JSX.Element => {
  const {
    rates: {
      sidepanel: { panelIsOpen, rate, rateHistory, pagination, errorsList },
    },
    exchangeDictionaries: { currencies, rateSource, activePermissions },
  } = useAppSelector((state) => state);

  const { currency, rateId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [rateError, setRateError] = useState('');
  const [globalError, setGlobalError] = useState('');

  const { search } = useLocation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
    watch,
    setError,
  } = useForm<RateFormValues>({
    defaultValues: {
      currencyLeftName: '',
      currencyRightName: '',
      currencyLeftId: '',
      currencyRightId: '',
      rateSourceId: '',
      value: undefined,
      decimals: 5,
      date: '',
    },
  });

  const [getRate, { isLoading, isFetching }] = useLazyGetRateQuery();
  const [
    getRateHistoryDetails,
    {
      isLoading: rateHistoryDetailsIsLoadng,
      isFetching: rateHistoryDetailsIsFetching,
      isSuccess,
    },
  ] = useLazyGetRateHistoryDetailsQuery();
  const [
    getRatesPair,
    { isLoading: ratesIsLoading, isFetching: ratesIsFetching, isError },
  ] = useLazyGetExchangePairRatesQuery();
  const [create, { isLoading: requestInProgress, isSuccess: submitSuccess }] =
    useCreateRateMutation();
  const [update, { isLoading: updateInProgress }] = useUpdateRateMutation();
  const [getList] = useLazyGetExchangeRatesQuery();

  const currencyLeftId = watch('currencyLeftId');

  const getExchangeRatesAction = useCallback(
    async (queryValue: string) => {
      getList(queryValue);
    },
    [currency],
  );

  const getRateHistoryDetailsAction = (id: string) => {
    dispatch(setExpandedRateHistoryId(id));
    getRateHistoryDetails(id);
  };

  const onSubmit = handleSubmit(async (values) => {
    let response;

    setGlobalError('');

    if (values.value && !Number.isNaN(+values.value)) {
      if (rateId !== 'create') {
        response = (await update({
          id: rateId as string,
          body: { value: Number(values.value) },
        })) as { error?: ResponseError };
      } else {
        response = (await create({
          currencyLeftName: values.currencyLeftName,
          currencyRightName: values.currencyRightName,
          currencyLeftId: values.currencyLeftId,
          currencyRightId: values.currencyRightId,
          rateSourceId: values.rateSourceId,
          value: Number(values.value),
          decimals: 5,
          date: new Date().toISOString(),
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    } else {
      setError('value', { type: 'custom', message: 'Rate is not a number' });
    }
  });

  const handleClose = () => {
    dispatch(clearRateSidepanelState());

    const url = generatePath(ROUTES.RATES_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const getRatesPairAction = useCallback(
    async ({
      currencyLeftIdValue,
      currencyRightId,
      date,
      rateSourceId,
      cursor,
      take,
    }: {
      currencyLeftIdValue: string;
      currencyRightId: string;
      date: string;
      rateSourceId: string;
      cursor?: string;
      take?: string;
    }) => {
      const response = await getRatesPair(
        `currencyLeftId=${currencyLeftIdValue}&currencyRightId=${currencyRightId}&rateSourceId=${rateSourceId}&date=${format(
          new Date(date),
          'yyyy-MM-dd',
        )}${cursor ? `&cursor=${cursor}` : ''}${
          take ? `&take=${take}` : '&take=10'
        }`,
      );

      if (response?.data?.data) {
        dispatch(setRateHistory(response?.data?.data));
      }
    },
    [],
  );

  useEffect(() => {
    if (rateId && rateId !== 'create') {
      getRate(rateId);
    }
  }, [rateId]);

  useEffect(() => {
    if (currency !== 'all') {
      const currentCurrency = currencies?.find((item) => item.id === currency);

      reset({
        currencyLeftId: currency,
        currencyLeftName: currentCurrency?.name,
      });
    }
  }, [currency, reset]);

  useEffect(() => {
    if (rate && !rateHistory) {
      setRateError('');
      reset(rate);

      getRatesPairAction({
        currencyLeftIdValue: rate.currencyLeftId,
        currencyRightId: rate.currencyRightId,
        rateSourceId: rate.rateSourceId,
        date: rate.date,
      });
    }
  }, [rate, rateHistory]);

  useEffect(() => {
    if (isError) {
      setRateError('Internal Server Error. Try Again');
    }
  }, [isError]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(clearRateSidepanelState());
    };
  }, []);

  useEffect(() => {
    if (submitSuccess) {
      handleClose();

      const queryValue =
        currency !== 'all' ? `?take=20&currencyLeftId=${currency}` : '?take=20';

      if (currency) {
        getExchangeRatesAction(queryValue);
      }
    }
  }, [submitSuccess]);

  let content = <div />;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_EXCHANGE_EDIT_PAIRS_ACTION,
    ) &&
    rateId === 'create'
  ) {
    content = <PermissionDenied />;
  } else if (
    rateId !== 'create' ||
    (activePermissions?.includes(
      PermissionEnum.SETTINGS_EXCHANGE_EDIT_PAIRS_ACTION,
    ) &&
      rateId === 'create')
  ) {
    if (isLoading || isFetching) {
      content = <SidePanelLoader />;
    } else {
      content = (
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <div className={cn.accountChangeLog}>
              <NavLink
                to={`/administration/change-logs?entityType=RATE&entityId=${rateId}`}
                target="_blank"
                className={cn.changeLogLink}
              >
                <IconChangeLoClock />
                Change Log
              </NavLink>
            </div>
          )}
          <form className={cn.createRateForm}>
            <SidePanelRow caption="Rate source">
              <Controller
                control={control}
                name="rateSourceId"
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    placeholder="Select rate source..."
                    name={name}
                    value={value}
                    list={rateSource?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    }))}
                    disabled={rateId !== 'create'}
                    onChange={(e) => onChange(e.target.value)}
                    error={errors?.rateSourceId?.message}
                    fullWidth
                  />
                )}
              />
            </SidePanelRow>
            {rateId === 'create' && (
              <>
                <SidePanelRow caption="Base currency">
                  <Controller
                    control={control}
                    name="currencyLeftId"
                    render={({ field: { onChange, value, name } }) => (
                      <Select
                        placeholder="Select currency..."
                        name={name}
                        value={value}
                        list={currencies?.map((item) => ({
                          name: `${item.name} (${item.description})`,
                          value: item.id,
                        }))}
                        onChange={(e) => {
                          onChange(e.target.value);

                          const currentCurrency = currencies?.find(
                            (item) => item.id === e.target.value,
                          );

                          setValue(
                            'currencyLeftName',
                            currentCurrency?.name as string,
                          );
                        }}
                        error={errors?.currencyLeftId?.message}
                        disabled={currency !== 'all'}
                        fullWidth
                      />
                    )}
                  />
                </SidePanelRow>

                {currencyLeftId && (
                  <SidePanelRow caption="Second currency">
                    <Controller
                      control={control}
                      name="currencyRightId"
                      render={({ field: { onChange, value, name } }) => (
                        <Select
                          placeholder="Select currency..."
                          name={name}
                          list={currencies
                            ?.filter((item) => item.id !== currencyLeftId)
                            .map((item) => ({
                              name: `${item.name} (${item.description})`,
                              value: item.id,
                            }))}
                          value={value}
                          onChange={(e) => {
                            onChange(e.target.value);

                            const currentCurrency = currencies?.find(
                              (item) => item.id === e.target.value,
                            );

                            setValue(
                              'currencyRightName',
                              currentCurrency?.name as string,
                            );
                          }}
                          error={errors?.currencyRightId?.message}
                          fullWidth
                        />
                      )}
                    />
                  </SidePanelRow>
                )}
              </>
            )}

            <SidePanelRow caption="Rate">
              <Controller
                control={control}
                name="value"
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    placeholder="Enter rate..."
                    name={name}
                    value={value}
                    onChange={onChange}
                    error={errors?.value?.message}
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.SETTINGS_EXCHANGE_EDIT_PAIRS_ACTION,
                      )
                    }
                    currency={{ decimals: 16 } as Currency}
                    amountInput
                  />
                )}
              />
            </SidePanelRow>
          </form>
          {rateId !== 'create' && (
            <>
              <div className={cn.historyRateTitle}>
                <span>
                  {rate?.currencyLeftName || ''} -{' '}
                  {rate?.currencyRightName || ''}
                </span>
                : exchange rate history
              </div>
              <HistoryRateList
                history={rateHistory}
                isLoading={ratesIsLoading}
                isFetching={ratesIsFetching}
                pagination={pagination}
                rateError={errorsList || rateError}
                getRateHistoryDetails={getRateHistoryDetailsAction}
                isSuccessExpandableRow={isSuccess}
                rateHistoryDetailsIsLoading={
                  rateHistoryDetailsIsLoadng || rateHistoryDetailsIsFetching
                }
                setPage={(cursorValue: { id: string; take: string }) =>
                  getRatesPairAction({
                    currencyLeftIdValue: rate?.currencyLeftId as string,
                    currencyRightId: rate?.currencyRightId as string,
                    rateSourceId: rate?.rateSourceId as string,
                    date: rate?.date as string,
                    cursor: cursorValue.id,
                    take: cursorValue.take,
                  })
                }
              />
            </>
          )}
        </>
      );
    }
  }

  let buttonText;

  if (rateId === 'create') {
    buttonText = requestInProgress ? 'Loading...' : 'Add Rate';
  } else {
    buttonText = updateInProgress ? 'Loading...' : 'Save';
  }

  let titleText: string;

  if (rateId === 'create') {
    titleText = 'Add Rate';
  } else if (isLoading || isFetching) {
    titleText = 'Loading...';
  } else if (rate?.currencyLeftName || rate?.currencyRightName) {
    titleText = `${rate?.currencyLeftName || ''}-${
      rate?.currencyRightName || ''
    } rate`;
  } else {
    titleText = 'Rate';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={titleText}
        className={cn.rateDetailsSidepanel}
        errorSidepanel={globalError}
        sidepanelExpandedKey="rate-details"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_EXCHANGE_EDIT_PAIRS_ACTION,
            ) && (
              <ButtonPrimary
                text={buttonText}
                onClick={onSubmit}
                disabled={requestInProgress}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RateDetails;
